import { type FC, useEffect, useState } from "react";
import Modal from "~/common/components/Modal";
import Spinner from "~/common/components/Spinner/Spinner";
import { Stacker } from "~/common/components/Stacker/Stacker";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { IconSize } from "~/common/components/icon/IconSize";
import { useTranslation } from "react-i18next";
import { Button } from "~/common/components/Button/Button";
import { Alert } from "~/common/m3/Alert/Alert";

interface manageModal {
  closeModal: () => void;
  addNewCard: () => void;
  updateCard: (data: CreditCard | undefined) => void;
  defaultCC?: CreditCard;
}

export interface CreditCard {
  cardHolderName?: string;
  cardName?: string;
  defaultCard: boolean;
  expMonth: string;
  expYear: string;
  expired: boolean;
  number: string;
  type: string;
  uid?: string;
}

const ManageCreditCard: FC<manageModal> = (props) => {
  const { t } = useTranslation();

  const { closeModal, addNewCard, updateCard, defaultCC } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [cards, setCards] = useState<Array<CreditCard>>([]);
  const [selectedCard, setSelectedCard] = useState<CreditCard>();

  const deleteCard = (id: string | undefined) => {
    fetch(`delete-card?uid=${id}`, { credentials: "same-origin", mode: "cors" })
      .then((res) => res.json())
      .then((json) => {
        setIsLoading(false);
        if (json === true) {
          const newArr: any = cards.filter((el: CreditCard) => el.uid !== id);
          if (defaultCC && defaultCC.uid === id) {
            updateCard(newArr[0] ? newArr[0] : undefined);
          }
          setCards(newArr);
        }
      })
      .catch(function () {
        setIsLoading(false);
      });
  };

  const changeDefaultCard = (name: string | undefined, id: string | undefined) => {
    fetch(`update-cc?uid=${id}&name=${name}&defaultCard=true`, { credentials: "same-origin", mode: "cors" })
      .then((res) => res.json())
      .then((json) => {
        if (json) {
          const newArr: any = cards.map((el: CreditCard, i: any) =>
            el.uid === id
              ? {
                  ...el,
                  defaultCard: true,
                }
              : {
                  ...el,
                  defaultCard: false,
                },
          );
          setCards(newArr);

          const selected = newArr.find((el: CreditCard) => el.defaultCard === true);
          setSelectedCard(selected);
          updateCard(selected);
        }
        setIsLoading(false);
      })
      .catch(function () {
        setIsLoading(false);
      });
  };

  const applyCardPayment = () => {
    if (selectedCard) {
      updateCard(selectedCard);
      closeModal();
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetch("view-cards", { credentials: "same-origin", mode: "cors" })
      .then((res) => res.json())
      .then((json) => {
        setCards(json);
        setIsLoading(false);
      })
      .catch(function () {
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (defaultCC) {
      setSelectedCard(defaultCC);
    }
  }, [defaultCC]);

  return (
    <Modal
      onClose={closeModal}
      title={t("checkoutPage.manageCreditCard.title")}
      footerChildren={
        <div className="d-flex flex-justify-between flex-align-center w-100">
          <Button onClick={() => addNewCard()} variant={"text"} data-test-id={"add-to-cart-btn-ok"}>
            <Icon className={"mr-1"} type={IconType.Plus} />
            {t("checkoutPage.addCreditCard.addNew")}
          </Button>
          <Stacker direction={"row"} gap={8}>
            <Button onClick={() => closeModal()} variant={"text"} data-test-id={"add-to-cart-btn-ok"}>
              {t("checkoutPage.addCreditCard.btnCancel")}
            </Button>
            <Button onClick={applyCardPayment} data-test-id={"add-to-cart-btn-ok"} disabled={cards.length === 0}>
              {t("checkoutPage.addCreditCard.btnApply")}
            </Button>
          </Stacker>
        </div>
      }
    >
      {isLoading && <Spinner />}
      {!isLoading && cards?.length > 0 && (
        <div className={"d-flex flex-direction-column"}>
          {cards.map((card: CreditCard, index: any) => (
            <>
              <div
                key={String(index)}
                className={
                  "mb-3 scha__order-summary-manage-credit-card-item" +
                  (card.uid === selectedCard?.uid && card.expired === false ? "__active" : "") +
                  (card.expired === true ? "__error" : "")
                }
                onClick={() => {
                  if (selectedCard?.uid !== card.uid && card.expired === false) {
                    setSelectedCard(card);
                  }
                }}
              >
                {card.uid === selectedCard?.uid && selectedCard?.expired !== true && (
                  <Icon
                    className={"scha__order-summary-manage-credit-card-item__icon"}
                    type={IconType.Check}
                    size={IconSize.FONT_16}
                  />
                )}
                <h2 className="title-large f-bold mt-0 mb-3">{card.cardName}</h2>
                <div className={"d-flex flex-justify-between mb-3"}>
                  <div
                    className={
                      "d-flex flex-direction-column scha__order-summary-manage-credit-card-item__title-options"
                    }
                  >
                    {t("checkoutPage.addCreditCard.nameCard")}
                    {card.cardHolderName && <p>{card.cardHolderName}</p>}
                  </div>
                  <div
                    className={
                      "d-flex flex-direction-column scha__order-summary-manage-credit-card-item__title-options"
                    }
                  >
                    {t("checkoutPage.addCreditCard.ccnumber")}
                    {card.number && <p>**** **** **** {card.number}</p>}
                  </div>
                  <div
                    className={
                      "d-flex flex-direction-column scha__order-summary-manage-credit-card-item__title-options"
                    }
                  >
                    {t("checkoutPage.addCreditCard.expDate")}
                    {card.expMonth && card.expYear && (
                      <p>
                        {card.expMonth}/{card.expYear}
                      </p>
                    )}
                  </div>
                  <div
                    className={
                      "d-flex flex-direction-column scha__order-summary-manage-credit-card-item__title-options"
                    }
                  >
                    {t("checkoutPage.addCreditCard.cardType")}
                    {card.type && <p className={"text-uppercase"}>{card.type}</p>}
                  </div>
                </div>
                <div className={"d-flex"}>
                  <div
                    className={
                      "scha__order-summary-manage-credit-card-item__btn-options" +
                      (card.expired || card.defaultCard ? "-disabled" : "")
                    }
                    onClick={() => card.expired === false && changeDefaultCard(card?.cardName, card.uid)}
                    data-test-id={""}
                  >
                    {card.defaultCard ? (
                      <>{t("checkoutPage.manageCreditCard.default")}</>
                    ) : (
                      <>{t("checkoutPage.manageCreditCard.setDefault")}</>
                    )}
                  </div>
                  <div
                    className={"scha__order-summary-manage-credit-card-item__btn-options"}
                    onClick={() => deleteCard(card.uid)}
                    data-test-id={""}
                  >
                    {t("checkoutPage.manageCreditCard.delete")}
                  </div>
                </div>
              </div>
              {card.expired === true && (
                <div className={"mb-2"}>
                  <Alert severity={"error"}>{t("checkoutPage.manageCreditCard.expired")}</Alert>
                </div>
              )}
            </>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default ManageCreditCard;
